import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, CheckCircle2, Send } from 'lucide-react';

import { addTestimonial } from 'redux/modules/testimonials';
import PhoneNumberField from 'components/PhoneVerification/PhoneNumberField';
import {
  Field,
  errorId,
  inputClass,
  invalidProps,
} from 'components/Portfolio/EntryDialog/EntryFields';
import fieldStyles from 'components/Portfolio/EntryDialog/EntryDialog.module.scss';
import { getErrorMessage, getFieldErrors } from 'helpers/apiErrors';
import styles from './TestimonialsAdd.module.scss';

const EMPTY = { name: '', company: '', email: '', project: '', phone: '' };

// API field names to form field names.
const API_FIELDS = {
  name: 'name',
  company_name: 'company',
  email: 'email',
  project_name: 'project',
  phone: 'phone',
};

export const getEmailError = (email) => {
  const value = email.trim();
  if (!value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return '';
  }
  if (/^[^\s@]+@[^\s@.]+$/.test(value)) {
    return 'Check the email address. It looks like it’s missing the ending, like .com';
  }
  return 'Enter an email address like name@company.com';
};

/** The form for asking a past client for a testimonial. */
const TestimonialsAdd = ({ onBack }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const sending = useSelector((state) => state.testimonials.addingTestimonial);
  const [fields, setFields] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [apiErrors, setApiErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [sentTo, setSentTo] = useState('');
  // Remounts the phone field so it clears after "Ask someone else".
  const [formKey, setFormKey] = useState(0);

  const set = (name, value) => {
    setFields((current) => ({ ...current, [name]: value }));
    setApiErrors((current) => ({ ...current, [name]: '' }));
  };
  const touch = (name) =>
    setTouched((current) => ({ ...current, [name]: true }));

  const emailError =
    apiErrors.email || (touched.email ? getEmailError(fields.email) : '');
  const nameError = apiErrors.name || '';
  const canSend =
    !!fields.name.trim() &&
    !!fields.email.trim() &&
    !getEmailError(fields.email) &&
    !sending;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSend) {
      touch('email');
      return;
    }
    setFormError('');
    dispatch(
      addTestimonial(
        fields.name,
        fields.company,
        fields.project,
        fields.email,
        fields.phone
      )
    ).then(
      () => setSentTo(fields.name.trim()),
      (error) => {
        const byField = getFieldErrors(error);
        const mapped = Object.keys(byField).reduce(
          (result, key) =>
            API_FIELDS[key]
              ? { ...result, [API_FIELDS[key]]: byField[key] }
              : result,
          {}
        );
        setApiErrors(mapped);
        if (Object.keys(mapped).length === 0) {
          setFormError(getErrorMessage(error));
        }
      }
    );
  };

  const askAnother = () => {
    setFields(EMPTY);
    setTouched({});
    setApiErrors({});
    setSentTo('');
    setFormKey((key) => key + 1);
  };

  const header = (
    <header className={styles.header}>
      <button
        type="button"
        className={styles.backBtn}
        onClick={onBack}
        aria-label="Back to testimonials"
      >
        <ArrowLeft size={18} strokeWidth={2} aria-hidden="true" />
      </button>
      <div className={styles.headerText}>
        <h2 className={styles.title}>Ask for a testimonial</h2>
        <p className={styles.subtitle}>
          We’ll email them a link to write it. It shows on your portfolio once
          they do.
        </p>
      </div>
    </header>
  );

  if (sentTo) {
    return (
      <div className={styles.root}>
        {header}
        <div className={styles.sent} role="status">
          <CheckCircle2 size={32} strokeWidth={2} aria-hidden="true" />
          <h3 className={styles.sentTitle}>Request sent to {sentTo}</h3>
          <p className={styles.sentText}>
            You’ll find it under Requests sent until they write it.
          </p>
        </div>
        <div className={fieldStyles.footer}>
          <div className={fieldStyles.footerActions}>
            <button
              type="button"
              className={fieldStyles.secondaryBtn}
              onClick={askAnother}
            >
              Ask someone else
            </button>
            <button
              type="button"
              className={fieldStyles.primaryBtn}
              onClick={onBack}
            >
              Back to testimonials
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.root} onSubmit={handleSubmit} noValidate>
      {header}
      <div className={fieldStyles.body}>
        <div className={fieldStyles.row}>
          <Field id="testimonial-name" label="Their name" error={nameError}>
            <input
              id="testimonial-name"
              type="text"
              className={inputClass(nameError)}
              value={fields.name}
              onChange={(event) => set('name', event.target.value)}
              autoComplete="off"
              maxLength={250}
              disabled={sending}
              {...invalidProps('testimonial-name', nameError)}
            />
          </Field>
          <Field
            id="testimonial-company"
            label="Company"
            optional
            error={apiErrors.company}
          >
            <input
              id="testimonial-company"
              type="text"
              className={inputClass(apiErrors.company)}
              value={fields.company}
              onChange={(event) => set('company', event.target.value)}
              autoComplete="off"
              maxLength={250}
              disabled={sending}
              {...invalidProps('testimonial-company', apiErrors.company)}
            />
          </Field>
        </div>
        <Field id="testimonial-email" label="Their email" error={emailError}>
          <input
            id="testimonial-email"
            type="email"
            className={inputClass(emailError)}
            value={fields.email}
            onChange={(event) => set('email', event.target.value)}
            onBlur={() => touch('email')}
            autoComplete="off"
            disabled={sending}
            {...invalidProps('testimonial-email', emailError)}
          />
        </Field>
        <div className={fieldStyles.field}>
          <label className={fieldStyles.label} htmlFor="testimonial-phone">
            Phone number
            <span className={fieldStyles.optional}>Optional</span>
          </label>
          <PhoneNumberField
            key={formKey}
            id="testimonial-phone"
            user={user}
            value={fields.phone}
            onChange={(phone) => set('phone', phone)}
            hasError={!!apiErrors.phone}
            disabled={sending}
          />
          {apiErrors.phone ? (
            <span
              id={errorId('testimonial-phone')}
              className={fieldStyles.error}
            >
              {apiErrors.phone}
            </span>
          ) : (
            <span className={fieldStyles.help}>
              Leave blank if you only have their email.
            </span>
          )}
        </div>
        <Field
          id="testimonial-project"
          label="Project you worked on"
          optional
          error={apiErrors.project}
          help="Reminds them what to write about, and shows on the testimonial."
        >
          <input
            id="testimonial-project"
            type="text"
            className={inputClass(apiErrors.project)}
            value={fields.project}
            onChange={(event) => set('project', event.target.value)}
            placeholder="For example, Coffee Co branding"
            maxLength={250}
            disabled={sending}
            {...invalidProps('testimonial-project', apiErrors.project)}
          />
        </Field>
        {formError && (
          <p className={fieldStyles.formError} role="alert">
            {formError}
          </p>
        )}
      </div>
      <div className={fieldStyles.footer}>
        <div className={fieldStyles.footerActions}>
          <button
            type="button"
            className={fieldStyles.secondaryBtn}
            onClick={onBack}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={fieldStyles.primaryBtn}
            disabled={!canSend}
          >
            <Send size={16} strokeWidth={2} aria-hidden="true" />
            {sending ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </form>
  );
};

TestimonialsAdd.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default TestimonialsAdd;
